import { useState } from 'react';
import { Settings } from './Settings';
import '@fortawesome/fontawesome-free/css/all.min.css';

interface SettingsState {
  useAuth: boolean;
  baseUrl: string;
  customEndpoint?: string;
}

interface RoomDetailsResponse {
  dailyRoom?: string;
  dailyToken?: string;
  room_url?: string;
  token?: string;
}

const DEFAULT_SETTINGS: SettingsState = {
  useAuth: true,
  baseUrl: 'https://wrlk8wwjm3.execute-api.us-east-1.amazonaws.com',
  customEndpoint: '/stage/sandraVoice'
};

function Sandravoice() {
  const [settings, setSettings] = useState<SettingsState>(DEFAULT_SETTINGS);
  const [showRoomLink, setShowRoomLink] = useState(false);
  const [roomDetails, setRoomDetails] = useState<RoomDetailsResponse | null>(null);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const handleMicClick = async () => {
    try {
      setIsLoading(true);
      setError(null);
      
      const apiEndpoint = getApiEndpoint();
      
      const headers: Record<string, string> = {
        'Content-Type': 'application/json'
      };

      if (settings.useAuth) {
        const token = localStorage.getItem('idToken');
        if (token) {
          headers['Authorization'] = `Bearer ${token}`;
        }
      }
      
      const response = await fetch(apiEndpoint, {
        method: 'POST',
        headers,
        body: JSON.stringify({ createDailyRoom: true })
      });
      
      if (!response.ok) {
        throw new Error(`API request failed with status ${response.status}`);
      }
      
      const data = await response.json();
      
      // Handle both response formats
      setRoomDetails({
        dailyRoom: data.dailyRoom || data.room_url,
        dailyToken: data.dailyToken || data.token
      });
      
      setShowRoomLink(true);
      setIsLoading(false);
      
    } catch (err) {
      console.error('Error fetching room details:', err);
      setError(err instanceof Error ? err.message : 'Unknown error');
      setIsLoading(false);
    }
  };

  const handleRoomLeave = () => {
    setShowRoomLink(false);
    setRoomDetails(null);
  };

  const getApiEndpoint = () => {
    // Use the same logic as the deleted RTVIProvider's getConnectEndpoint method
    if (window.location.pathname.includes("sophia")) {
      return `${settings.baseUrl}/stage/Sandraseragent`;
    }
    
    if (settings.baseUrl === 'https://wrlk8wwjm3.execute-api.us-east-1.amazonaws.com') {
      return `${settings.baseUrl}/stage/sandraVoice`;
    }
    
    // For other base URLs (like NOVANT_DEMO_URL), use the custom endpoint
    return `${settings.baseUrl}${settings.customEndpoint || '/stage/sandraVoice'}`;
  };

  const getDailyRoomUrl = () => {
    if (!roomDetails) return '';
    const roomUrl = roomDetails.dailyRoom || roomDetails.room_url;
    const token = roomDetails.dailyToken || roomDetails.token;
    return `${roomUrl}?t=${token}`;
  };

  return (
    <div className="min-h-screen bg-gray-100">
      <div className="container mx-auto px-4 py-8">
        <Settings onSettingsChange={setSettings} />
        
        {!showRoomLink && (
          <div
            style={{
              height: "100%",
              width: "100%",
              justifyContent: "center"
            }}
            className={`bg-gray-100 flex sm:items-center items-start pt-6`}
          >
            <div className="text-center">
              <button
                onClick={handleMicClick}
                disabled={isLoading}
                className="flex items-center justify-center w-20 h-20 bg-gray-400 text-white rounded-full shadow-lg hover:bg-gray-500 transition-colors cursor-pointer mx-auto disabled:opacity-50 disabled:cursor-not-allowed"
                title="Click to join Daily room"
              >
                {isLoading ? (
                  <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-white"></div>
                ) : (
                  <svg 
                    className="w-10 h-10" 
                    fill="none" 
                    stroke="currentColor" 
                    viewBox="0 0 24 24"
                  >
                    <path 
                      strokeLinecap="round" 
                      strokeLinejoin="round" 
                      strokeWidth={2} 
                      d="M19 11a7 7 0 01-7 7m0 0a7 7 0 01-7-7m7 7v4m0 0H8m4 0h4m-4-8a3 3 0 01-3-3V5a3 3 0 116 0v6a3 3 0 01-3 3z" 
                    />
                  </svg>
                )}
              </button>
              <div className="mt-2 text-red-600 font-medium">
                {isLoading ? 'Creating room...' : 'Not Listening'}
              </div>
            </div>
          </div>
        )}
        
        {error && (
          <div className="mt-4 p-4 bg-red-100 border border-red-400 text-red-700 rounded max-w-md mx-auto">
            <div className="font-bold mb-2">Error:</div>
            <div className="mb-4">{error}</div>
            <button 
              onClick={handleMicClick}
              className="px-4 py-2 bg-red-600 text-white rounded hover:bg-red-700"
            >
              Try Again
            </button>
          </div>
        )}
        
        {showRoomLink && roomDetails && (
          <div className="mt-4 p-4 bg-white rounded-lg shadow max-w-md mx-auto">
            <h2 className="text-xl font-semibold mb-4 text-center">Daily Room Link</h2>
            <div className="text-center mb-4">
              <a 
                href={getDailyRoomUrl()} 
                target="_blank" 
                rel="noopener noreferrer"
                className="text-blue-600 hover:text-blue-800 underline text-lg font-medium"
              >
                Join Daily Room
              </a>
            </div>
            <div className="text-center">
              <button
                onClick={handleRoomLeave}
                className="px-4 py-2 bg-gray-600 text-white rounded hover:bg-gray-700"
              >
                Back to Mic
              </button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}

export default Sandravoice;

